/**
 * Aircraft damage-location catalog for the SkyDent edit modal.
 *
 * Mirrors SkyDent's two-level location picker: the user first chooses a top-level
 * zone (a fuselage/wing/empennage/cargo area), then a refined location within it.
 * The stored value SkyDent expects is `"<code> - <label>"` (e.g.
 * "301 - Vertical Stabilizer"), so {@link locationValue} builds that string.
 *
 * The codes, labels, and thumbnail slug logic are transcribed verbatim from
 * SkyDent's `src/data/aircraftLocations.ts` and `locationToFilename` so the
 * stored values and thumbnails match SkyDent exactly. Thumbnails are served from
 * `public/location-thumbnails/<slug>.png` (same asset set and path as SkyDent).
 * Keep this file in sync if SkyDent changes its location list.
 */

/** A single refined damage location within a category. */
export interface AircraftLocation {
  /** SkyDent location code, e.g. "301". */
  code: string;
  /** Human-readable location label, e.g. "Vertical Stabilizer". */
  label: string;
}

/** A top-level category (zone) grouping refined locations. */
export interface AircraftLocationCategory {
  /** Category code, e.g. "300". */
  code: string;
  /** Category label, e.g. "Empennage and Tail". */
  label: string;
  locations: AircraftLocation[];
}

/** Builds the canonical stored value SkyDent expects: `"<code> - <label>"`. */
export function locationValue(location: AircraftLocation): string {
  return `${location.code} - ${location.label}`;
}

/**
 * Converts a stored `"<code> - <label>"` value to SkyDent's thumbnail filename
 * slug. Transcribed verbatim from SkyDent's `locationToFilename`: strip the
 * leading code, lowercase, collapse whitespace/hyphens to single hyphens, and
 * drop any remaining non-alphanumeric characters.
 */
export function locationToFilename(value: string): string {
  return value
    .replace(/^\d+\s*-\s*/, '')
    .toLowerCase()
    .replace(/[\s-]+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
}

/**
 * Resolves the thumbnail URL for a location, mirroring SkyDent's
 * `/location-thumbnails/<slug>.png` scheme. Callers should hide the image on
 * load error (some slugs may lack an asset).
 */
export function resolveLocationThumbnail(location: AircraftLocation): string {
  const base = process.env.PUBLIC_URL || '';
  return `${base}/location-thumbnails/${locationToFilename(locationValue(location))}.png`;
}

/** The zone catalog rendered by the SkyDent location picker (verbatim from SkyDent). */
export const AIRCRAFT_LOCATION_CATEGORIES: readonly AircraftLocationCategory[] = [
  {
    code: '50',
    label: 'Radome',
    locations: [{ code: '50', label: 'Radome' }],
  },
  {
    code: '100',
    label: 'Lower Fuselage Below WL 0.0',
    // SkyDent's source has a duplicate code 104 (Section 46 and Section 48); preserved for fidelity.
    locations: [
      { code: '101', label: 'Lower Fuselage - Section 41' },
      { code: '102', label: 'Lower Fuselage - Section 43' },
      { code: '103', label: 'Lower Fuselage - Section 45' },
      { code: '104', label: 'Lower Fuselage - Section 46' },
      { code: '104', label: 'Lower Fuselage - Section 48' },
    ],
  },
  {
    code: '200',
    label: 'Upper Fuselage - Above WL 0.0',
    locations: [
      { code: '201', label: 'Upper Fuselage - Section 41' },
      { code: '202', label: 'Upper Fuselage - Section 43' },
      { code: '203', label: 'Upper Fuselage - Section 45' },
      { code: '204', label: 'Upper Fuselage - Section 46' },
      { code: '205', label: 'Upper Fuselage - Section 48' },
    ],
  },
  {
    code: '300',
    label: 'Empennage and Tail',
    locations: [
      { code: '301', label: 'Vertical Stabilizer' },
      { code: '302', label: 'LH Horizontal Stabilizer' },
      { code: '303', label: 'RH Horizontal Stabilizer' },
    ],
  },
  {
    code: '400',
    label: 'Engine and Nacelle Strut',
    locations: [
      { code: '401', label: 'LH Engine' },
      { code: '402', label: 'LH Engine Inlet' },
      { code: '403', label: 'LH Engine and Nacelle Strut, Inboard' },
      { code: '404', label: 'LH Engine and Nacelle Strut, Outboard' },
      { code: '405', label: 'RH Engine' },
      { code: '406', label: 'RH Engine Inlet' },
      { code: '407', label: 'RH Engine and Nacelle Strut, Inboard' },
      { code: '408', label: 'RH Engine and Nacelle Strut, Outboard' },
    ],
  },
  {
    code: '500',
    label: 'Left Wing',
    locations: [
      { code: '501', label: 'Left Wing - #1 Slat Vicinity, Upper Skin' },
      { code: '502', label: 'Left Wing - #1 Slat Vicinity, Lower Skin' },
      { code: '503', label: 'Left Wing - #2 Slat Vicinity, Upper Skin' },
      { code: '504', label: 'Left Wing - #2 Slat Vicinity, Lower Skin' },
      { code: '505', label: 'Left Wing - #3 Slat Vicinity, Upper Skin' },
      { code: '506', label: 'Left Wing - #3 Slat Vicinity, Lower Skin' },
      { code: '507', label: 'Left Wing - #4 Slat Vicinity, Upper Skin' },
      { code: '508', label: 'Left Wing - #4 Slat Vicinity, Lower Skin' },
      { code: '509', label: 'Left Wing - #5 Slat Vicinity, Upper Skin' },
      { code: '510', label: 'Left Wing - #5 Slat Vicinity, Lower Skin' },
      { code: '511', label: 'Left Wing - #6 Slat Vicinity, Upper Skin' },
      { code: '512', label: 'Left Wing - #6 Slat Vicinity, Lower Skin' },
    ],
  },
  {
    code: '600',
    label: 'Right Wing',
    locations: [
      { code: '601', label: 'Right Wing - #1 Slat Vicinity, Upper Skin' },
      { code: '602', label: 'Right Wing - #1 Slat Vicinity, Lower Skin' },
      { code: '603', label: 'Right Wing - #2 Slat Vicinity, Upper Skin' },
      { code: '604', label: 'Right Wing - #2 Slat Vicinity, Lower Skin' },
      { code: '605', label: 'Right Wing - #3 Slat Vicinity, Upper Skin' },
      { code: '606', label: 'Right Wing - #3 Slat Vicinity, Lower Skin' },
      { code: '607', label: 'Right Wing - #4 Slat Vicinity, Upper Skin' },
      { code: '608', label: 'Right Wing - #4 Slat Vicinity, Lower Skin' },
      { code: '609', label: 'Right Wing - #5 Slat Vicinity, Upper Skin' },
      { code: '610', label: 'Right Wing - #5 Slat Vicinity, Lower Skin' },
      { code: '611', label: 'Right Wing - #6 Slat Vicinity, Upper Skin' },
      { code: '612', label: 'Right Wing - #6 Slat Vicinity, Lower Skin' },
    ],
  },
  {
    code: '700',
    label: 'Landing Gear and Doors',
    locations: [
      { code: '701', label: 'Nose Landing Gear and Doors' },
      { code: '702', label: 'LH Main Landing Gear and Doors' },
      { code: '703', label: 'RH Main Landing Gear and Doors' },
    ],
  },
  {
    code: '800',
    label: 'Cargo Compartment',
    locations: [
      { code: '801', label: 'Main Cargo Compartment - Section 41, Deck' },
      { code: '802', label: 'Main Cargo Compartment - Section 41, LH Wall' },
      { code: '803', label: 'Main Cargo Compartment - Section 41, RH Wall' },
      { code: '804', label: 'Main Cargo Compartment - Section 41, Ceiling' },
      { code: '805', label: 'Main Cargo Compartment - Section 43, Deck' },
      { code: '806', label: 'Main Cargo Compartment - Section 43, LH Wall' },
      { code: '807', label: 'Main Cargo Compartment - Section 43, RH Wall' },
      { code: '808', label: 'Main Cargo Compartment - Section 43, Ceiling' },
      { code: '809', label: 'Main Cargo Compartment - Section 45, Deck' },
      { code: '810', label: 'Main Cargo Compartment - Section 45, LH Wall' },
      { code: '811', label: 'Main Cargo Compartment - Section 45, RH Wall' },
      { code: '812', label: 'Main Cargo Compartment - Section 45, Ceiling' },
      { code: '813', label: 'Main Cargo Compartment - Section 46, Deck' },
      { code: '814', label: 'Main Cargo Compartment - Section 46, LH Wall' },
      { code: '815', label: 'Main Cargo Compartment - Section 46, RH Wall' },
      { code: '816', label: 'Main Cargo Compartment - Section 46, Ceiling' },
      { code: '817', label: 'Main Cargo Door' },
      { code: '818', label: 'Main Cargo Door - Seal Depressor' },
      { code: '819', label: 'Fwd Lower Cargo Compartment - Deck' },
      { code: '820', label: 'Fwd Lower Cargo Compartment - LH Wall' },
      { code: '821', label: 'Fwd Lower Cargo Compartment - RH Wall' },
      { code: '822', label: 'Fwd Lower Cargo Compartment - Ceiling' },
      { code: '823', label: 'Fwd Lower Cargo Door' },
      { code: '824', label: 'Fwd Lower Cargo Door - Seal Depressor' },
      { code: '825', label: 'Aft Lower Cargo Compartment - Deck' },
      { code: '826', label: 'Aft Lower Cargo Compartment - LH Wall' },
      { code: '827', label: 'Aft Lower Cargo Compartment - RH Wall' },
      { code: '828', label: 'Aft Lower Cargo Compartment - Ceiling' },
      { code: '829', label: 'Aft Lower Cargo Door' },
      { code: '830', label: 'Aft Lower Cargo Door - Seal Depressor' },
      { code: '831', label: 'Bulk Cargo Compartment' },
    ],
  },
];

/**
 * Finds the category + location matching a stored `"<code> - <label>"` value.
 * Returns null when the value is empty or not in the catalog (e.g. a legacy or
 * SkyDent-only value), so the modal can still display the raw value.
 */
export function findLocationByValue(
  value: string,
): { category: AircraftLocationCategory; location: AircraftLocation } | null {
  if (!value) return null;
  for (const category of AIRCRAFT_LOCATION_CATEGORIES) {
    for (const location of category.locations) {
      if (locationValue(location) === value) return { category, location };
    }
  }
  return null;
}
